import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {Collaborator, COLLABORATOR_SHAPE, Ownable, HasOwnablePermissions} from 'auth/PermissionConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {EditWorkflowTemplateMetadataResponse} from 'workflows/dao/WorkflowTemplateDAO';
import {WORKFLOW_TEMPLATE_FULL_SHAPE, WorkflowTemplate} from 'workflows/WorkflowConstants';
import * as WorkflowTemplateActions from 'workflows/WorkflowTemplateActions';

import WorkflowTemplateBaseSettingsModal from './WorkflowTemplateBaseSettingsModal';

interface WorkflowTemplateSettingsModalBaseProps {
  onRequestClose: () => void;
  workflowTemplate: WorkflowTemplate;
}

interface EnhancedProps {
  collaborators?: Array<Collaborator>;
}

function mapStateToProps(
  state: BenchlingState,
  {workflowTemplate}: WorkflowTemplateSettingsModalBaseProps
): EnhancedProps {
  return {
    collaborators: state.models.collaboratorsByOwnableId[workflowTemplate.id],
  };
}

class WorkflowTemplateSettingsModal extends React.Component<
  WorkflowTemplateSettingsModalBaseProps & EnhancedProps
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    // Props to specify when rendering.
    onRequestClose: PropTypes.func.isRequired,
    // If workflowTemplate is specified the modal is editing the existing workflow template
    workflowTemplate: WORKFLOW_TEMPLATE_FULL_SHAPE.isRequired,

    // Props provided by connect.
    collaborators: PropTypes.arrayOf(COLLABORATOR_SHAPE.isRequired),
  };

  componentDidMount(): void {
    if (this._getExistingOwnable() == null) {
      // We only fetch the collaborators if they are not found in the store. If the collaborators are
      // already in the store we assume that they are up to date, since OwnableSettingsModal doesn't
      // support updating collaborators after it has been mounted.
      WorkflowTemplateActions.fetchCollaborators(this.props.workflowTemplate.id, {
        onFailure: this.props.onRequestClose,
      });
    }
  }

  _handleDeleteWorkflowTemplate = (): JQueryPromise<{}> =>
    WorkflowTemplateActions.deleteWorkflowTemplate(this.props.workflowTemplate.id);

  _handleRequestSubmit = ({
    name,
    prefix,
    owner,
    ownerInfo,
    collaborators,
  }: Ownable): JQueryPromise<EditWorkflowTemplateMetadataResponse> =>
    WorkflowTemplateActions.editWorkflowTemplateMetadata(this.props.workflowTemplate.id, {
      name,
      owner,
      ownerInfo,
      prefix,
      collaborators,
    });

  _getExistingOwnable(): (Ownable & Pick<HasOwnablePermissions, 'permissions'>) | null {
    if (this.props.collaborators == null) {
      // Collaborators weren't loaded yet
      return null;
    }
    return {
      name: this.props.workflowTemplate.name,
      prefix: this.props.workflowTemplate.prefix,
      description: '',
      owner: this.props.workflowTemplate.owner,
      ownerInfo: this.props.workflowTemplate.ownerInfo,
      collaborators: this.props.collaborators.filter(({owner}) => !owner),
      permissions: this.props.workflowTemplate.permissions,
    };
  }

  render(): JSX.Element | null {
    const existingOwnable = this._getExistingOwnable();
    if (existingOwnable == null) {
      // Collaborators are still loading
      return null;
    }
    return (
      <WorkflowTemplateBaseSettingsModal
        ownableId={this.props.workflowTemplate.id}
        existingOwnable={existingOwnable}
        onRequestClose={this.props.onRequestClose}
        doSubmit={this._handleRequestSubmit}
        doDelete={this._handleDeleteWorkflowTemplate}
      />
    );
  }
}

export default connect(mapStateToProps)(WorkflowTemplateSettingsModal);
